import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { message, Form, Input, Button, Tooltip, DatePicker, Space, Typography, Select, Switch } from 'antd';
import { CloseOutlined, SaveOutlined, InfoCircleOutlined, DownOutlined, UpOutlined, CloudUploadOutlined, LockOutlined } from '@ant-design/icons';
import axios from '../../utility/axios';
import { findComposeContact, normalizePhoneDigits } from '../../utility/composeContactHelpers';
import { useComposeContacts, filterContacts } from '../../hooks/useComposeContacts';
import ContactsDropdown from '../compose/ContactsDropdown';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../../styles/messages/MessageCompose.css';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { useUserSettings } from '../../hooks/useUserSettings';
import MessageEditor from './MessageEditor';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import { ContactGroupModal } from '../settings';
import AccessCodeDeliveryModal from '../compose/AccessCodeDeliveryModal';
import { useTranslation } from 'react-i18next';
import { isConvertible } from '../../utility/fileTypes';

// Initialize dayjs plugins
dayjs.extend(relativeTime);

const { Text } = Typography;

const flattenDraftComments = (list) => {
  if (!Array.isArray(list)) return [];
  const out = [];
  const visit = (c) => {
    out.push({ ...c, replies: undefined });
    (c.replies || []).forEach(visit);
  };
  list.forEach(visit);
  return out;
};

const MessageCompose = ({ initialValues, isReply }) => {
    const { isLoggedIn, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { settings, loading: settingsLoading } = useSystemSettings();
    
    // Helper function to check if we're working with a template
    const isTemplate = () => {
        const urlParams = new URLSearchParams(window.location.search);
        return !!urlParams.get('template_id');
    };
    const { userSettings, loading: userSettingsLoading } = useUserSettings();
    const { t } = useTranslation();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [form] = Form.useForm();
    const [hasRecipients, setHasRecipients] = useState(false);
    // Add states for filtering contacts
    const [recipientSearchText, setRecipientSearchText] = useState('');
    const [ccSearchText, setCcSearchText] = useState('');
    const [bccSearchText, setBccSearchText] = useState('');

    // Add states for tag values - to avoid using form.getFieldValue directly in Select
    const [recipientTags, setRecipientTags] = useState([]);
    const [ccTags, setCcTags] = useState([]);
    const [bccTags, setBccTags] = useState([]);

    // Flag to prevent onChange validation when selecting a contact
    const [selectingContact, setSelectingContact] = useState(false);
    
    // Track dropdown state for each field
    const [recipientDropdownOpen, setRecipientDropdownOpen] = useState(false);
    const [ccDropdownOpen, setCcDropdownOpen] = useState(false);
    const [bccDropdownOpen, setBccDropdownOpen] = useState(false);
    
    // Update auto-save functionality states
    const [draftId, setDraftId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedTime, setLastSavedTime] = useState(null);
    const [displayTimestamp, setDisplayTimestamp] = useState('');
    const autoSaveTimeoutRef = useRef(null);
    const initialLoadRef = useRef(true);
    const formModifiedRef = useRef(false);
    const saveDraftRef = useRef(null);
    const updateDisplayTimestampRef = useRef(null);
    
    const [accessCodeDeliveryModalOpen, setAccessCodeDeliveryModalOpen] = useState(false);
    const [accessCodeDelivery, setAccessCodeDelivery] = useState({});
    const [accessCodeSmsPhones, setAccessCodeSmsPhones] = useState({});
    const [accessCodeBtnError, setAccessCodeBtnError] = useState(false);
    const contactPhoneSnapshotRef = useRef({});
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
    
    // Add state to track if we're loading a draft from localStorage
    const [loadingDraft, setLoadingDraft] = useState(false);
    const draftLoadedRef = useRef(false);
    
    // Add state for limited editing mode
    const [isLimitedEdit, setIsLimitedEdit] = useState(false);
    const [limitedEditMessageId, setLimitedEditMessageId] = useState(null);
    const [limitedEditFiles, setLimitedEditFiles] = useState([]);
    
    // Add state for CC/BCC visibility
    const [showCcBcc, setShowCcBcc] = useState(false);
    
    // Add state to track if this draft was originally a sent message
    const [isOriginallySentMessage, setIsOriginallySentMessage] = useState(false);
    
    // Add state to track if user came from sent folder (vs drafts folder)
    const [cameFromSentFolder, setCameFromSentFolder] = useState(false);
    
    // Add states for user settings widgets
    const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
    const [currentSignature, setCurrentSignature] = useState('');
    const [preventCopyEnabled, setPreventCopyEnabled] = useState(false);
    
    // Add ref for MessageEditor
    const messageEditorRef = useRef(null);
    
    // Add state for drag and drop
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const dragCounterRef = useRef(0);
    
    // Add state for collapsible form top section
    const [formTopCollapsed, setFormTopCollapsed] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    // Attachment preview modal (for local files before send)
    const [attachmentPreviewFile, setAttachmentPreviewFile] = useState(null);
    const [localFileComments, setLocalFileComments] = useState({});
    const [draftFileComments, setDraftFileComments] = useState({});
    
    // Initialize user settings values
    useEffect(() => {
        if (userSettings) {
            setEmailNotificationsEnabled(userSettings.notification_settings?.email_notifications_enabled ?? true);
            setCurrentSignature(userSettings.profile_settings?.email_signature || '');
        }
    }, [userSettings]);

    useEffect(() => {
        if (!isLoggedIn) return;
        const fetchTemplates = async () => {
            setTemplatesLoading(true);
            try {
                const res = await axios.get('/api/message/templates/');
                setTemplates(res.data?.templates || []);
            } catch (err) {
                console.error('Failed to fetch templates:', err);
                setTemplates([]);
            } finally {
                setTemplatesLoading(false);
            }
        };
        fetchTemplates();
    }, [isLoggedIn]);
    
    // Check for limited edit mode
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const limitedEditId = urlParams.get('limited_edit');
        
        // Check where the user came from (sent folder vs drafts folder)
        const editSource = localStorage.getItem('editSource');
        if (editSource === 'sent') {
            setCameFromSentFolder(true);
        }
        
        if (limitedEditId) {
            // Get the limited edit data from localStorage
            const limitedEditData = JSON.parse(localStorage.getItem('limitedEditMessage') || '{}');
            
            if (limitedEditData && limitedEditData.limitedEdit) {
                setIsLimitedEdit(true);
                setLimitedEditMessageId(limitedEditData.messageId);
                
                const applyMessageData = (messageData) => {
                    const recipientArray = messageData.recipient 
                        ? messageData.recipient.split(',').map(email => email.trim()).filter(email => email !== '')
                        : [];
                    const ccArray = messageData.cc
                        ? messageData.cc.split(',').map(email => email.trim()).filter(email => email !== '')
                        : [];
                    const bccArray = messageData.bcc
                        ? messageData.bcc.split(',').map(email => email.trim()).filter(email => email !== '')
                        : [];
                    setRecipientTags(recipientArray);
                    setCcTags(ccArray);
                    setBccTags(bccArray);
                    setHasRecipients(recipientArray.length > 0);
                    setTimeout(() => {
                        form.setFieldsValue({
                            subject: messageData.subject || '',
                            message: messageData.body || '',
                            recipient: recipientArray,
                            cc: ccArray,
                            bcc: bccArray,
                            self_destruct: limitedEditData.self_destruct || false,
                            timestamp: limitedEditData.ttlive ? dayjs(limitedEditData.ttlive) : null,
                        });
                        if (limitedEditData.prevent_message_copy !== undefined) {
                            setPreventCopyEnabled(limitedEditData.prevent_message_copy);
                        }
                    }, 50);
                };

                const setFilesFromMessage = (messageData) => {
                    const files = messageData.files || messageData.attachments || [];
                    if (Array.isArray(files) && files.length > 0) {
                        setLimitedEditFiles(files.map((f) => ({
                            uid: f.unique_identifier || f.uid,
                            name: f.file_name || f.title || f.name || 'file',
                            size: f.file_size ?? f.fileSize ?? 0,
                            file_size: f.file_size ?? f.fileSize ?? 0,
                            documentFileId: f.unique_identifier || f.uid,
                            source: 'messages',
                            comment_count: f.comment_count ?? 0,
                        })));
                    }
                };

                const fetchMessageDetails = async () => {
                    try {
                        const response = await axios.get(`/api/link/preview/${limitedEditData.messageId}/`);
                        const messageData = response.data.message || {};
                        applyMessageData(messageData);
                        setFilesFromMessage(messageData);
                    } catch (error) {
                        console.error('Error fetching message details:', error);
                        if (limitedEditData.messageData) {
                            applyMessageData(limitedEditData.messageData);
                            setFilesFromMessage(limitedEditData.messageData);
                        } else {
                            message.error(t('messages.errors.failedToFetch'));
                        }
                    }
                };

                if (limitedEditData.messageData) {
                    applyMessageData(limitedEditData.messageData);
                    setFilesFromMessage(limitedEditData.messageData);
                }
                fetchMessageDetails();
                
                // When submitting in limited edit mode, we'll use a different endpoint
                draftLoadedRef.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);
    
    // Load saved draft ID from localStorage
    useEffect(() => {
        if (!authLoading && isLoggedIn && !draftLoadedRef.current) {
            const loadDraftFromLocalStorage = async () => {
                try {
                    // Get saved draft ID from localStorage
                    const savedDraftId = localStorage.getItem('currentDraftId');
                    const urlParams = new URLSearchParams(window.location.search);
                    const draftIdFromUrl = urlParams.get('draft_id');
                    const templateIdFromUrl = urlParams.get('template_id');
                    
                    // Prioritize template_id or draft_id from URL, then initialValues, then localStorage
                    const draftIdToLoad = templateIdFromUrl || draftIdFromUrl || initialValues?.draft_id || savedDraftId;
                    
                    // Only load a draft if we have an ID and haven't already loaded one
                    if (draftIdToLoad && !draftLoadedRef.current) {
                        setLoadingDraft(true);
                        
                        // Fetch the draft from backend
                        const draft = await fetchDraft(draftIdToLoad);
                        
                        if (draft) {
                            // Set the draft ID
                            setDraftId(draft.unique_identifier);
                            
                            // Parse recipients - use the recipient, cc, bcc fields directly from the draft
                            const recipientArray = draft.recipient 
                                ? draft.recipient.split(',').map(email => email.trim()).filter(email => email !== '')
                                : [];
                                
                            setRecipientTags(recipientArray);
                            setHasRecipients(recipientArray.length > 0);
                            
                            // Parse CC
                            const ccArray = draft.cc
                                ? draft.cc.split(',').map(email => email.trim()).filter(email => email !== '')
                                : [];
                            setCcTags(ccArray);
                            
                            // Parse BCC
                            const bccArray = draft.bcc
                                ? draft.bcc.split(',').map(email => email.trim()).filter(email => email !== '')
                                : [];
                            setBccTags(bccArray);
                            
                            // Set expiry time
                            let expiryTime = null;
                            if (draft.ttlive) {
                                expiryTime = dayjs(draft.ttlive);
                            }
                            
                            // Set form values
                            form.setFieldsValue({
                                subject: draft.subject || '',
                                message: draft.body || '',
                                self_destruct: draft.self_destruct || false,
                                timestamp: expiryTime || null,
                                recipient: recipientArray,
                                cc: ccArray,
                                bcc: bccArray
                            });
                            
                            // Set prevent copy state from draft
                            if (draft.prevent_message_copy !== undefined) {
                                setPreventCopyEnabled(draft.prevent_message_copy);
                            }
                            
                            // Set last saved time
                            if (draft.updated_at) {
                                const savedTime = dayjs(draft.updated_at);
                                setLastSavedTime(savedTime);
                                updateDisplayTimestamp(savedTime);
                            }
                            
                            // Enable auto-save for drafts, but NOT for templates
                            const urlParams = new URLSearchParams(window.location.search);
                            const isLoadingTemplate = !!urlParams.get('template_id');
                            
                            if (!isLoadingTemplate) {
                                setAutoSaveEnabled(true);
                                console.log('Auto-save enabled for loaded draft:', draft.unique_identifier);
                            } else {
                                console.log('Auto-save disabled for template:', draft.unique_identifier);
                            }
                            
                            if (draft.files && Array.isArray(draft.files) && draft.files.length > 0) {
                                const fileListForEditor = draft.files.map((f) => ({
                                    uid: f.unique_identifier,
                                    name: f.file_name,
                                    size: f.file_size || 0,
                                    status: 'done',
                                    isDraftFile: true,
                                    download_url: f.download_url || `/api/drafts/files/${f.unique_identifier}/`,
                                }));
                                setTimeout(() => messageEditorRef.current?.setFileList(fileListForEditor), 0);
                                const commentsByFile = {};
                                draft.files.forEach((f) => {
                                    if (f.unique_identifier && Array.isArray(f.comments)) {
                                        commentsByFile[f.unique_identifier] = f.comments;
                                    }
                                });
                                setDraftFileComments((prev) => ({ ...prev, ...commentsByFile }));
                            }

                            draftLoadedRef.current = true;
                            localStorage.setItem('currentDraftId', draft.unique_identifier);
                        }
                        
                        setLoadingDraft(false);
                    }
                } catch (error) {
                    console.error('Error loading draft:', error);
                    setLoadingDraft(false);
                }
            };
            
            loadDraftFromLocalStorage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isLoggedIn, form, settings, initialValues?.draft_id]);

    // Single useEffect to handle form initialization
    useEffect(() => {
        if (!settingsLoading && settings && !userSettingsLoading && userSettings && !draftLoadedRef.current) {
            // Use user settings for defaults - no fallback needed since defaults are set in database
            const messageLifetimeDays = userSettings.message_security?.message_lifetime_days;
            const dailyExpirationTime = userSettings.message_security?.daily_expiration_time;
            
            // Parse the daily expiration time from user settings
            const [hours, minutes] = dailyExpirationTime.split(':').map(Number);
            const defaultExpiry = dayjs()
                .add(messageLifetimeDays, 'days')
                .hour(hours)
                .minute(minutes)
                .second(0);

            // Process recipient to handle both string (comma-separated) and single values
            let recipientArray = [];
            let ccArray = [];
            let bccArray = [];

            // Check if initialValues contains a draft_id (for existing drafts passed as props)
            if (initialValues?.draft_id) {
                setDraftId(initialValues.draft_id);
                localStorage.setItem('currentDraftId', initialValues.draft_id);
                // Enable auto-save since this is an existing draft
                setAutoSaveEnabled(true);
                console.log('Auto-save enabled for initialValues draft:', initialValues.draft_id);
                draftLoadedRef.current = true;
            }

            // Process recipient emails
            if (initialValues?.recipient) {
                if (typeof initialValues.recipient === 'string') {
                    // Split by comma, semicolon, or space and filter out empty values
                    recipientArray = initialValues.recipient
                        .split(/[,;\s]+/)
                        .map(email => email.trim())
                        .filter(email => email !== '');
                } else if (initialValues.recipient) {
                    // Handle single value or array
                    const recipients = Array.isArray(initialValues.recipient) 
                        ? initialValues.recipient 
                        : [initialValues.recipient];
                    recipientArray = recipients.filter(email => email && typeof email === 'string' && email.trim() !== '');
                }
            }

            // Update has recipients state
            setHasRecipients(recipientArray.length > 0);
            setRecipientTags(recipientArray);  // Set the state-driven tags

            // Process CC emails
            if (initialValues?.cc) {
                if (typeof initialValues.cc === 'string') {
                    ccArray = initialValues.cc
                        .split(/[,;\s]+/)
                        .map(email => email.trim())
                        .filter(email => email !== '');
                } else if (initialValues.cc) {
                    const ccs = Array.isArray(initialValues.cc) 
                        ? initialValues.cc 
                        : [initialValues.cc];
                    ccArray = ccs.filter(email => email && typeof email === 'string' && email.trim() !== '');
                }
            }
            setCcTags(ccArray);  // Set the state-driven tags

            // Process BCC emails
            if (initialValues?.bcc) {
                if (typeof initialValues.bcc === 'string') {
                    bccArray = initialValues.bcc
                        .split(/[,;\s]+/)
                        .map(email => email.trim())
                        .filter(email => email !== '');
                } else if (initialValues.bcc) {
                    const bccs = Array.isArray(initialValues.bcc) 
                        ? initialValues.bcc 
                        : [initialValues.bcc];
                    bccArray = bccs.filter(email => email && typeof email === 'string' && email.trim() !== '');
                }
            }
            setBccTags(bccArray);  // Set the state-driven tags

            const defaultValues = {
                self_destruct: false,
                timestamp: defaultExpiry,
                ...initialValues,
                recipient: recipientArray, // Override with properly formatted recipient array
                cc: ccArray,
                bcc: bccArray
            };

            form.setFieldsValue(defaultValues);
            
            // Set prevent copy state from initialValues
            if (initialValues?.prevent_message_copy !== undefined) {
                setPreventCopyEnabled(initialValues.prevent_message_copy);
            }
            
            // If initialValues had updated_at, set the last saved time
            if (initialValues?.updated_at) {
                const savedTime = dayjs(initialValues.updated_at);
                setLastSavedTime(savedTime);
                updateDisplayTimestamp(savedTime);
            }
            
            // After a slight delay, mark the form as initialized
            setTimeout(() => {
                initialLoadRef.current = false;
            }, 1000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, settings, userSettingsLoading, userSettings, form, initialValues]);

    useEffect(() => {
        if (!authLoading && !isLoggedIn) {
            navigate('/login?redirect=/message-compose');
        }
    }, [isLoggedIn, authLoading, navigate]);

    const {
        contacts,
        contactsLoading,
        setContacts,
        fetchContacts,
        updateContactsApi,
        contactEditModalVisible,
        contactEditingContact,
        handleContactAddFavorite,
        handleContactRemoveFavorite,
        handleContactEdit,
        handleContactDelete,
        handleContactEditModalClose,
        handleContactEditModalSaved,
    } = useComposeContacts({ apiEndpoint: '/api/compose/contacts/', isLoggedIn, authLoading, t });

    useEffect(() => {
        const all = [...recipientTags, ...ccTags, ...bccTags].map((e) => String(e).trim()).filter(Boolean);
        setAccessCodeSmsPhones((prev) => {
            const next = { ...prev };
            all.forEach((email) => {
                if (next[email] === undefined) {
                    const c = findComposeContact(contacts, email);
                    if (c?.phone) next[email] = c.phone;
                }
            });
            return next;
        });
    }, [recipientTags, ccTags, bccTags, contacts]);

    useEffect(() => {
        if (!accessCodeDeliveryModalOpen) return;
        const all = [...recipientTags, ...ccTags, ...bccTags].map((e) => String(e).trim()).filter(Boolean);
        const snap = {};
        all.forEach((email) => {
            const c = findComposeContact(contacts, email);
            if (c) snap[email] = c.phone || '';
        });
        contactPhoneSnapshotRef.current = snap;
        setAccessCodeSmsPhones((prev) => {
            const next = { ...prev };
            all.forEach((email) => {
                if (next[email] === undefined && snap[email]) {
                    next[email] = snap[email];
                }
            });
            return next;
        });
    }, [accessCodeDeliveryModalOpen, recipientTags, ccTags, bccTags, contacts]);

    const filteredRecipientContacts = useMemo(() =>
        filterContacts(contacts, recipientSearchText),
        [contacts, recipientSearchText]
    );

    const filteredCcContacts = useMemo(() =>
        filterContacts(contacts, ccSearchText),
        [contacts, ccSearchText]
    );

    const filteredBccContacts = useMemo(() =>
        filterContacts(contacts, bccSearchText),
        [contacts, bccSearchText]
    );

    // Auto-save functionality
    const saveDraft = useCallback(async () => {
        if (!formModifiedRef.current || initialLoadRef.current) {
            return;
        }
        
        try {
            setIsSaving(true);
            const formValues = form.getFieldsValue();
            
            // Process recipients
            let recipientValue = '';
            let ccValue = '';
            let bccValue = '';
            
            if (formValues.recipient && Array.isArray(formValues.recipient)) {
                recipientValue = formValues.recipient.join(', ');
            } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                recipientValue = formValues.recipient.trim();
            }
            
            if (formValues.cc && Array.isArray(formValues.cc)) {
                ccValue = formValues.cc.join(', ');
            } else if (formValues.cc && typeof formValues.cc === 'string') {
                ccValue = formValues.cc.trim();
            }
            
            if (formValues.bcc && Array.isArray(formValues.bcc)) {
                bccValue = formValues.bcc.join(', ');
            } else if (formValues.bcc && typeof formValues.bcc === 'string') {
                bccValue = formValues.bcc.trim();
            }
            
            // Prepare data for sending
            const params = new URLSearchParams();
            if (draftId) {
                params.append('draft_id', draftId);
            }
            params.append('subject', formValues.subject || '');
            params.append('message', formValues.message || '');
            
            // Always include recipient, cc, and bcc even if empty
            params.append('recipient', recipientValue);
            params.append('cc', ccValue);
            params.append('bcc', bccValue);
            
            // If we're editing a template, mark it as template
            if (isTemplate()) {
                params.append('is_template', 'true');
            }
            
            params.append('self_destruct', formValues.self_destruct || false);
            if (formValues.timestamp) {
                params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            params.append('prevent_message_copy', preventCopyEnabled.toString());

            const fileList = messageEditorRef.current?.getFileList() || [];
            const draftFileIds = fileList.filter((f) => f.isDraftFile).map((f) => f.uid);
            const newFiles = fileList.filter((f) => !f.isDraftFile && (f.originFileObj || f));
            params.append('existing_draft_file_ids', JSON.stringify(draftFileIds));
            const attachmentDraftCommentsPayload = {};
            draftFileIds.forEach((fid) => {
                const list = draftFileComments[fid];
                if (Array.isArray(list) && list.length > 0) {
                    attachmentDraftCommentsPayload[fid] = flattenDraftComments(list).map((c) => ({
                        unique_identifier: c.unique_identifier,
                        content: c.content,
                        page: c.page ?? 1,
                        position_x: c.position_x ?? 50,
                        position_y: c.position_y ?? 50,
                        width: c.width ?? 25,
                        height: c.height ?? 10,
                        parent_comment_id: c.parent_comment_id || null,
                    }));
                }
            });
            params.append('attachment_draft_comments', JSON.stringify(attachmentDraftCommentsPayload));
            const attachmentLocalCommentsPayload = newFiles.map((f) => {
                const list = localFileComments[f.uid] || [];
                return Array.isArray(list) ? list.map((c) => ({
                    content: c.content,
                    page: c.page ?? 1,
                    position_x: c.position_x ?? 50,
                    position_y: c.position_y ?? 50,
                    width: c.width ?? 25,
                    height: c.height ?? 10,
                })) : [];
            });
            params.append('attachment_local_comments', JSON.stringify(attachmentLocalCommentsPayload));

            let response;
            if (newFiles.length > 0) {
                const formData = new FormData();
                for (const [k, v] of params.entries()) formData.append(k, v);
                newFiles.forEach((f) => {
                    const raw = f.originFileObj || f;
                    if (raw) formData.append('attachments', raw, raw.name || f.name || 'file');
                });
                response = await axios.post('/api/save_message_draft/', formData);
            } else {
                response = await axios.post('/api/save_message_draft/', params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });
            }

            if (!draftId && response.data.draft_id) {
                setDraftId(response.data.draft_id);
                localStorage.setItem('currentDraftId', response.data.draft_id);
            }
            if (response.data.updated_at) {
                const savedTime = dayjs(response.data.updated_at);
                setLastSavedTime(savedTime);
                updateDisplayTimestamp(savedTime);
            }
            if (draftId && newFiles.length > 0) {
                const refreshed = await fetchDraft(draftId);
                if (refreshed?.files?.length !== undefined) {
                    const fileListForEditor = refreshed.files.map((f) => ({
                        uid: f.unique_identifier,
                        name: f.file_name,
                        size: f.file_size || 0,
                        status: 'done',
                        isDraftFile: true,
                        download_url: f.download_url || `/api/drafts/files/${f.unique_identifier}/`,
                    }));
                    messageEditorRef.current?.setFileList(fileListForEditor);
                    const commentsByFile = {};
                    refreshed.files.forEach((f) => {
                        if (f.unique_identifier && Array.isArray(f.comments)) {
                            commentsByFile[f.unique_identifier] = f.comments;
                        }
                    });
                    setDraftFileComments((prev) => ({ ...prev, ...commentsByFile }));
                }
            }
            setTimeout(() => setIsSaving(false), 3000);
            formModifiedRef.current = false;
        } catch (error) {
            console.error('Error saving draft:', error);
            setIsSaving(false);
            message.error(t('messages.errors.saveFailed'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, draftId, emailNotificationsEnabled, preventCopyEnabled, draftFileComments, localFileComments]);

    useEffect(() => {
        saveDraftRef.current = saveDraft;
    }, [saveDraft]);

    // Function to update the display timestamp
    const updateDisplayTimestamp = useCallback((savedTime) => {
        if (!savedTime) return;
        
        const now = dayjs();
        const timeAgo = savedTime.from(now);
        setDisplayTimestamp(timeAgo);
    }, []);
    
    // Set up an interval to update the "time ago" display
    useEffect(() => {
        if (lastSavedTime) {
            // Update the timestamp display immediately
            updateDisplayTimestamp(lastSavedTime);
            
            // Set up an interval to update the timestamp every minute
            updateDisplayTimestampRef.current = setInterval(() => {
                updateDisplayTimestamp(lastSavedTime);
            }, 60000); // Update every minute
        }
        
        return () => {
            if (updateDisplayTimestampRef.current) {
                clearInterval(updateDisplayTimestampRef.current);
            }
        };
    }, [lastSavedTime, updateDisplayTimestamp]);
    
    const triggerAutoSave = useCallback(() => {
        // Only trigger auto-save if it's enabled AND we're not editing a template
        if (!autoSaveEnabled || isTemplate()) {
            console.log('Auto-save triggered but not enabled or editing template, skipping');
            return;
        }
        
        console.log('Auto-save triggered, setting timeout for 5 seconds');
        
        // Clear any existing timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }
        
        // Set a new timeout to save after 5 seconds of inactivity
        autoSaveTimeoutRef.current = setTimeout(() => {
            console.log('Auto-save timeout reached, saving draft');
            saveDraft();
        }, 5000);
    }, [saveDraft, autoSaveEnabled]);

    // Persist draft file comments when they change (AttachmentPreview only updates local state)
    const handleDraftFileCommentsChange = useCallback((fileUid, comments) => {
        setDraftFileComments((prev) => ({ ...prev, [fileUid]: comments }));
        formModifiedRef.current = true;
        if (draftId) {
            const shouldSaveNow = (autoSaveEnabled && !isTemplate()) || isTemplate();
            if (shouldSaveNow) {
                setTimeout(() => saveDraftRef.current?.(), 150);
            } else {
                triggerAutoSave();
            }
        }
    }, [draftId, autoSaveEnabled, triggerAutoSave, isTemplate]);

    // Set up form field change monitoring for auto-save
    useEffect(() => {
        // Initial load of form values
        form.getFieldsValue();
        
        // We're not using form.subscribe since it doesn't exist
        // Instead, we're relying on:
        // 1. onValuesChange prop on the Form component
        // 2. User activity event listeners (keydown, click, input, change)
        
        // Clear timeout on unmount
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [form, triggerAutoSave]);
    
    // After initial form data is loaded, mark initialLoad as complete
    useEffect(() => {
        if (!settingsLoading && settings && initialLoadRef.current) {
            // After form is initialized, mark initialLoad as complete
            setTimeout(() => {
                initialLoadRef.current = false;
            }, 1000);
        }
    }, [settingsLoading, settings]);

    // Add an effect to monitor user activity across form fields
    useEffect(() => {
        const resetAutoSaveTimeout = () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
                autoSaveTimeoutRef.current = setTimeout(() => {
                    saveDraft();
                }, 5000);
            }
        };
        
        // Add event listeners to capture user activity
        const formElement = document.querySelector('.message-compose-detail-body-wrapper form');
        if (formElement) {
            formElement.addEventListener('keydown', resetAutoSaveTimeout);
            formElement.addEventListener('click', resetAutoSaveTimeout);
            formElement.addEventListener('input', resetAutoSaveTimeout);
            formElement.addEventListener('change', resetAutoSaveTimeout);
        }
        
        return () => {
            if (formElement) {
                formElement.removeEventListener('keydown', resetAutoSaveTimeout);
                formElement.removeEventListener('click', resetAutoSaveTimeout);
                formElement.removeEventListener('input', resetAutoSaveTimeout);
                formElement.removeEventListener('change', resetAutoSaveTimeout);
            }
        };
    }, [saveDraft]);

    // Drag and drop handlers for file attachments
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Only respond to file drags, not other drags
        if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
            dragCounterRef.current++;
            if (dragCounterRef.current === 1) {
                setIsDraggingFiles(true);
            }
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        dragCounterRef.current--;
        if (dragCounterRef.current === 0) {
            setIsDraggingFiles(false);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        dragCounterRef.current = 0;
        setIsDraggingFiles(false);
        
        // Don't allow drops in limited edit mode
        if (isLimitedEdit) return;
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) return;
        
        // Get current file list from editor
        const currentFileList = messageEditorRef.current?.getFileList() || [];
        
        // Validate and add each file
        const allowedTypes = settings?.allowedFileTypes?.split(',') || [];
        const maxSize = (settings?.maxUploadSize || 10) * 1024 * 1024;
        
        const validFiles = [];
        for (const file of files) {
            const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
            
            if (file.size >= maxSize) {
                message.error(t('messages.validation.fileTooLarge', { size: settings?.maxUploadSize || 10 }));
                continue;
            }
            
            if (allowedTypes.length > 0 && !allowedTypes.includes(fileExtension)) {
                message.error(t('messages.validation.fileTypeNotSupported', { types: settings?.allowedFileTypes || '' }));
                continue;
            }
            
            // Create a file object compatible with Ant Design Upload
            validFiles.push({
                uid: `${Date.now()}-${file.name}`,
                name: file.name,
                size: file.size,
                type: file.type,
                originFileObj: file,
                status: 'done',
            });
        }
        
        if (validFiles.length > 0) {
            const newFileList = [...currentFileList, ...validFiles];
            messageEditorRef.current?.setFileList(newFileList);
            message.success(t('messages.send.filesAdded', { count: validFiles.length }));
        }
    }, [isLimitedEdit, settings, t]);

    if (authLoading || !isLoggedIn) return null;

    // Add a function to fetch a draft by ID
    const fetchDraft = async (draftId) => {
        try {
            // Check if this is a template by looking at URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const isTemplate = urlParams.get('template_id') || urlParams.get('folder') === 'templates';
            
            // Use appropriate endpoint based on whether it's a template or draft
            const endpoint = isTemplate ? `/api/get_message_template/${draftId}/` : `/api/get_message_draft/${draftId}/`;
            const response = await axios.get(endpoint);
            
            // Check if this draft has recipients (indicating it was originally a sent message)
            // True drafts from the drafts table won't have message_receivers, but sent messages moved to drafts will
            const draft = response.data.draft;
            if (draft && (draft.recipient || draft.cc || draft.bcc)) {
                setIsOriginallySentMessage(true);
            }
            
            return draft;
        } catch (error) {
            console.error('Error fetching draft:', error);
            return null;
        }
    };

    // Handler for contact selection
    const handleContactSelect = (email, field, isGroup = false) => {
        // Set flag to prevent onChange validation - set it before we modify any state
        setSelectingContact(true);
        
        // Get current values from state
        let currentValues = [];
        
        if (field === 'recipient') {
            currentValues = [...recipientTags];
            setRecipientSearchText('');
        } else if (field === 'cc') {
            currentValues = [...ccTags];
            setCcSearchText('');
        } else if (field === 'bcc') {
            currentValues = [...bccTags];
            setBccSearchText('');
        }

        // Capture count before this selection to decide whether to close the dropdown
        const wasEmpty = currentValues.length === 0;
        
        // If it's a group, split the email string by semicolon to get individual emails
        const emailsToProcess = isGroup 
            ? email.split(';').map(e => e.trim()).filter(e => e !== '')
            : [email];
        
        // Check if ALL emails from the group are already in currentValues
        const allEmailsExist = emailsToProcess.every(e => currentValues.includes(e));
        
        if (allEmailsExist) {
            // If all emails exist, remove them all (toggle off)
            emailsToProcess.forEach(e => {
                const index = currentValues.indexOf(e);
                if (index >= 0) {
                    currentValues.splice(index, 1);
                }
            });
        } else {
            // If not all emails exist, add all missing ones
            emailsToProcess.forEach(e => {
                if (!currentValues.includes(e)) {
                    currentValues.push(e);
                }
            });
        }
        
        // Use setTimeout to ensure state updates are processed before form updates
        setTimeout(() => {
            // Update form directly and sync with state
            form.setFieldsValue({ [field]: currentValues });
            
            // Update recipient-specific state
            if (field === 'recipient') {
                setRecipientTags(currentValues);
                setHasRecipients(currentValues.length > 0);
                
                // Clear CC and BCC if no recipients
                if (currentValues.length === 0) {
                    form.setFieldsValue({ cc: [], bcc: [] });
                    setCcTags([]);
                    setBccTags([]);
                }
            } else if (field === 'cc') {
                setCcTags(currentValues);
            } else if (field === 'bcc') {
                setBccTags(currentValues);
            }
            
            // Mark form as modified to trigger auto-save
            formModifiedRef.current = true;
            triggerAutoSave();

            // Close dropdown after first contact added; keep open for subsequent picks
            if (wasEmpty) {
                if (field === 'recipient') setRecipientDropdownOpen(false);
                else if (field === 'cc') setCcDropdownOpen(false);
                else if (field === 'bcc') setBccDropdownOpen(false);
            }

            // Reset flag after onChange has completed
            setTimeout(() => {
                setSelectingContact(false);
            }, 100);
        }, 10);
    };

    const handleAccessCodeModalSave = async () => {
        const all = [...recipientTags, ...ccTags, ...bccTags].map((e) => String(e).trim()).filter(Boolean);
        const snap = contactPhoneSnapshotRef.current || {};
        for (const email of all) {
            const c = findComposeContact(contacts, email);
            if (!c?.id) continue;
            const method = accessCodeDelivery[email] ?? 'sms';
            if (method !== 'sms') continue;
            const current = accessCodeSmsPhones[email] ?? '';
            const before = snap[email] ?? '';
            if (normalizePhoneDigits(current) === normalizePhoneDigits(before)) continue;
            try {
                await updateContactsApi({
                    ids: [c.id],
                    action: 'update',
                    email: c.email,
                    firstname: c.firstname || '',
                    lastname: c.lastname || '',
                    phone: current,
                });
                setContacts((prev) =>
                    prev.map((x) => (x._type === 'contact' && x.id === c.id ? { ...x, phone: current } : x))
                );
            } catch (err) {
                console.error(err);
                message.error(t('compose.accessCodeDelivery.contactUpdateFailed'));
                return;
            }
        }
        setAccessCodeDeliveryModalOpen(false);
    };

    // Process email pattern validation
    const validateEmail = (email) => {
        if (!email || typeof email !== 'string') {
            return false;
        }
        
        // Trim any whitespace
        const trimmedEmail = email.trim();
        if (trimmedEmail === '') {
            return false;
        }
        
        // Check for basic email format - must have @ with something before and after, and a domain with at least one period
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
        return isValid;
    };

    // Add a dedicated function for limited editing
    const handleLimitedEdit = async (values) => {
        setSubmitLoading(true);
        try {
            // Use URLSearchParams for the limited edit request (same as regular submit)
            const params = new URLSearchParams();
            
            params.append('self_destruct', values.self_destruct || false);
            
            if (values.timestamp) {
                params.append('timestamp', values.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            
            
            // Send the update request to a specialized endpoint for updating sent messages
            await axios.post(`/api/message/update/${limitedEditMessageId}/`, params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            // Clear localStorage and limited edit state
            localStorage.removeItem('limitedEditMessage');
            
            // Navigate back to the mailbox
            navigate('/profile/mailbox');
        } catch (error) {
            console.error('Error updating message:', error);
            message.error(t('messages.errors.sendFailed'));
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        // If in limited edit mode, use the specialized function
        if (isLimitedEdit) {
            return handleLimitedEdit(values);
        }

        const allEmails = [
            ...(recipientTags || []),
            ...(ccTags || []),
            ...(bccTags || []),
        ].map((e) => String(e).trim()).filter(Boolean);
        const missingSmsPhone = allEmails.some(
            (email) => (accessCodeDelivery[email] ?? 'sms') === 'sms' && !accessCodeSmsPhones[email]?.trim()
        );
        if (missingSmsPhone) {
            message.error(t('compose.accessCodeDelivery.smsPhoneMissing'));
            setAccessCodeBtnError(true);
            setTimeout(() => setAccessCodeBtnError(false), 1000);
            return;
        }

        setSubmitLoading(true);
        try {
            // Fallback: fill missing fields from current form state (draft-loaded forms with
            // custom components like recipient Tags/Select may not fully sync to onFinish)
            const formValues = form.getFieldsValue?.() || {};
            const merged = { ...values };
            ['subject', 'message', 'timestamp', 'recipient', 'cc', 'bcc', 'self_destruct'].forEach(key => {
                const v = merged[key];
                const f = formValues[key];
                const isEmpty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
                const formHasValue = f !== undefined && f !== null && f !== '' && (Array.isArray(f) ? f.length > 0 : true);
                if (isEmpty && formHasValue) merged[key] = f;
            });
            if ((!merged.recipient || (Array.isArray(merged.recipient) && merged.recipient.length === 0)) && recipientTags?.length > 0) {
                merged.recipient = recipientTags;
            }
            values = merged;

            // Get the recipient string - ensure proper handling of all formats
            let recipientValue = '';
            let ccValue = '';
            let bccValue = '';
            
            // Handle array of recipients
            if (values.recipient && Array.isArray(values.recipient) && values.recipient.length > 0) {
                // Join with comma and space
                recipientValue = values.recipient.join(', ');
            } 
            // Handle single string recipient
            else if (values.recipient && typeof values.recipient === 'string' && values.recipient.trim() !== '') {
                recipientValue = values.recipient.trim();
            }
            
            // Handle CC emails
            if (values.cc && Array.isArray(values.cc) && values.cc.length > 0) {
                ccValue = values.cc.join(', ');
            } 
            else if (values.cc && typeof values.cc === 'string' && values.cc.trim() !== '') {
                ccValue = values.cc.trim();
            }
            
            // Handle BCC emails
            if (values.bcc && Array.isArray(values.bcc) && values.bcc.length > 0) {
                bccValue = values.bcc.join(', ');
            } 
            else if (values.bcc && typeof values.bcc === 'string' && values.bcc.trim() !== '') {
                bccValue = values.bcc.trim();
            }
            
            // Use URLSearchParams for simple x-www-form-urlencoded data
            const params = new URLSearchParams();
            params.append('subject', values.subject || '');
            params.append('message', values.message || '');
            params.append('self_destruct', values.self_destruct || false);
            params.append('recipient', recipientValue);
            params.append('cc', ccValue);
            params.append('bcc', bccValue);
            
            // Always include expiry timestamp (required by backend create_email)
            const messageLifetimeDays = userSettings?.message_security?.message_lifetime_days ?? 1;
            const [hours, minutes] = (userSettings?.message_security?.daily_expiration_time || '23:59:59').split(':').map(Number);
            const defaultExpiryTimestamp = dayjs().add(messageLifetimeDays, 'days').hour(hours || 23).minute(minutes || 59).second(0);
            params.append('timestamp', (values.timestamp || defaultExpiryTimestamp).format('YYYY-MM-DD HH:mm:ss'));
            
            
            // Include email notifications setting
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            
            // Include prevent copy setting
            params.append('prevent_message_copy', preventCopyEnabled.toString());
            
            const isTemplateCheck = isTemplate();
            if (isTemplateCheck) {
                params.append('template_id', draftId);
            } else if (draftId) {
                params.append('draft_id', draftId);
            }

            const allEmails = [...(recipientValue ? recipientValue.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean) : []),
                ...(ccValue ? ccValue.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean) : []),
                ...(bccValue ? bccValue.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean) : [])];
            const deliveryToSend = {};
            const phonesToSend = {};
            allEmails.forEach((email) => {
                deliveryToSend[email] = accessCodeDelivery[email] ?? 'sms';
                if (deliveryToSend[email] === 'sms' && accessCodeSmsPhones[email]) {
                    phonesToSend[email] = accessCodeSmsPhones[email];
                }
            });
            params.append('access_code_delivery', JSON.stringify(deliveryToSend));
            if (Object.keys(phonesToSend).length > 0) {
                params.append('access_code_sms_phones', JSON.stringify(phonesToSend));
            }

            // Get file list from MessageEditor
            const fileList = messageEditorRef.current?.getFileList() || [];
            
            // Handle files separately with FormData if needed
            let hasAttachments = fileList.length > 0;
            let response;
            
            if (hasAttachments) {
                const formData = new FormData();
                // Append all params (subject, recipient, message, timestamp, etc. - same as DocumensoCompose)
                for (const [key, value] of params.entries()) {
                    formData.append(key, value);
                }
                
                // Add files - only append actual File/Blob (new uploads). Draft files are referenced by draft_id/template_id.
                fileList.forEach(file => {
                    if (file.isDraftFile) return;
                    const fileToSend = file.originFileObj || file;
                    if (fileToSend && (fileToSend instanceof File || fileToSend instanceof Blob)) {
                        const name = fileToSend.name || file.name || 'file';
                        formData.append('attachments', fileToSend, name);
                    }
                });
                
                // Tell backend which draft files to keep (user may have removed some). When user removes all and adds new, this is [].
                const keptDraftFileIds = fileList.filter((f) => f.isDraftFile).map((f) => f.uid);
                if (draftId) {
                    formData.append('existing_draft_file_ids', JSON.stringify(keptDraftFileIds));
                }
                
                // Include draft_id or template_id in FormData as well
                if (isTemplateCheck) {
                    formData.append('template_id', draftId);
                } else if (draftId) {
                    formData.append('draft_id', draftId);
                }
                
                // Include email notifications setting in FormData
                formData.append('email_notifications_enabled', emailNotificationsEnabled.toString());
                
                formData.append('prevent_message_copy', preventCopyEnabled.toString());
                const commentsPayload = fileList.map((f) => {
                    const comments = f.isDraftFile ? (draftFileComments[f.uid] || []) : (localFileComments[f.uid] || []);
                    return comments.map((c) => ({
                        content: c.content,
                        page: c.page,
                        position_x: c.position_x,
                        position_y: c.position_y,
                        width: c.width,
                        height: c.height,
                    }));
                });
                formData.append('attachment_comments', JSON.stringify(commentsPayload));
                response = await axios.post('/api/send_ttl_message/', formData);
            } else {
                // If no attachments, use URLSearchParams directly
                response = await axios.post('/api/send_ttl_message/', params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });
            }
            
            // Store unique identifier before resetting
            const uniqueIdentifier = response.data.unique_identifier;
            
            // Clear localStorage draft ID
            localStorage.removeItem('currentDraftId');
            localStorage.removeItem('editSource');
            
            // If we were editing a draft (not a template), we should delete the draft from the server since it's now sent
            // Templates should remain intact after sending
            if (draftId && !isTemplateCheck) {
                try {
                    await axios.delete(`/api/delete_message_draft/${draftId}/`);
                } catch (error) {
                    console.error('Error deleting draft after sending:', error);
                    // Non-critical error, we can continue
                }
            }
            
            form.resetFields();
            messageEditorRef.current?.clearFileList();
            setLocalFileComments({});
            setDraftFileComments({});
            setRecipientTags([]);
            setCcTags([]);
            setBccTags([]);
            setHasRecipients(false);
            setDraftId(null);
            formModifiedRef.current = false;
            
            navigate('/profile/mailbox');
        } catch (error) {
            console.error('Error sending message:', error);
            message.error(t('messages.errors.sendFailed'));
        } finally {
            setSubmitLoading(false);
        }
    };

    // Add function to handle "Save as Draft" button click
    const handleSaveAsDraft = async () => {
        try {
            // Mark as modified to ensure it saves
            formModifiedRef.current = true;
            
            // If we're editing a template, always create a new draft (don't update the template)
            const urlParams = new URLSearchParams(window.location.search);
            const isEditingTemplate = !!urlParams.get('template_id');
            
            if (isEditingTemplate) {
                // Create a new draft from template data - don't pass draft_id to avoid updating template
                const formValues = form.getFieldsValue();
                
                // Process recipients
                let recipientValue = '';
                let ccValue = '';
                let bccValue = '';
                
                if (formValues.recipient && Array.isArray(formValues.recipient)) {
                    recipientValue = formValues.recipient.join(', ');
                } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                    recipientValue = formValues.recipient.trim();
                }
                
                if (formValues.cc && Array.isArray(formValues.cc)) {
                    ccValue = formValues.cc.join(', ');
                } else if (formValues.cc && typeof formValues.cc === 'string') {
                    ccValue = formValues.cc.trim();
                }
                
                if (formValues.bcc && Array.isArray(formValues.bcc)) {
                    bccValue = formValues.bcc.join(', ');
                } else if (formValues.bcc && typeof formValues.bcc === 'string') {
                    bccValue = formValues.bcc.trim();
                }
                
                // Prepare data for sending - NO draft_id, template_id for backend to copy template files
                const params = new URLSearchParams();
                params.append('template_id', draftId);
                params.append('subject', formValues.subject || '');
                params.append('message', formValues.message || '');
                params.append('recipient', recipientValue);
                params.append('cc', ccValue);
                params.append('bcc', bccValue);
                params.append('self_destruct', formValues.self_destruct || false);
                if (formValues.timestamp) {
                    params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
                }
                params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
                params.append('prevent_message_copy', preventCopyEnabled.toString());

                const fileList = messageEditorRef.current?.getFileList() || [];
                const draftFileIds = fileList.filter((f) => f.isDraftFile).map((f) => f.uid);
                const newFiles = fileList.filter((f) => !f.isDraftFile && (f.originFileObj || f));
                params.append('existing_draft_file_ids', JSON.stringify(draftFileIds));
                const attachmentDraftCommentsPayload = {};
                draftFileIds.forEach((fid) => {
                    const list = draftFileComments[fid];
                    if (Array.isArray(list) && list.length > 0) {
                        attachmentDraftCommentsPayload[fid] = flattenDraftComments(list).map((c) => ({
                            unique_identifier: c.unique_identifier,
                            content: c.content,
                            page: c.page ?? 1,
                            position_x: c.position_x ?? 50,
                            position_y: c.position_y ?? 50,
                            width: c.width ?? 25,
                            height: c.height ?? 10,
                            parent_comment_id: c.parent_comment_id || null,
                        }));
                    }
                });
                params.append('attachment_draft_comments', JSON.stringify(attachmentDraftCommentsPayload));
                const attachmentLocalCommentsPayloadSaveAs = newFiles.map((f) => {
                    const list = localFileComments[f.uid] || [];
                    return Array.isArray(list) ? list.map((c) => ({
                        content: c.content,
                        page: c.page ?? 1,
                        position_x: c.position_x ?? 50,
                        position_y: c.position_y ?? 50,
                        width: c.width ?? 25,
                        height: c.height ?? 10,
                    })) : [];
                });
                params.append('attachment_local_comments', JSON.stringify(attachmentLocalCommentsPayloadSaveAs));

                let response;
                if (newFiles.length > 0) {
                    const formData = new FormData();
                    for (const [k, v] of params.entries()) formData.append(k, v);
                    newFiles.forEach((f) => {
                        const raw = f.originFileObj || f;
                        if (raw) formData.append('attachments', raw, raw.name || f.name || 'file');
                    });
                    response = await axios.post('/api/save_message_draft/', formData);
                } else {
                    response = await axios.post('/api/save_message_draft/', params, {
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                    });
                }
                
                // Update to the new draft ID and enable auto-save
                if (response.data.draft_id) {
                    setDraftId(response.data.draft_id);
                    localStorage.setItem('currentDraftId', response.data.draft_id);
                    setAutoSaveEnabled(true);
                    
                    // Update URL to reflect we're now editing a draft, not a template
                    const newUrl = `/message-compose?draft_id=${response.data.draft_id}`;
                    window.history.replaceState({}, '', newUrl);
                }
                
                message.success(t('messages.send.draftCreatedFromTemplate'));
            } else {
                // Normal draft save/update logic
                const isUpdating = draftId && autoSaveEnabled;
                console.log('Save as Draft clicked. Is updating:', isUpdating, 'Draft ID:', draftId, 'Auto-save enabled:', autoSaveEnabled);
                
                // Enable auto-save for future changes if not already enabled
                if (!autoSaveEnabled) {
                    setAutoSaveEnabled(true);
                    console.log('Auto-save enabled via Save as Draft button');
                }
                
                // Save immediately
                await saveDraft();
            }
            
            // Draft status indicators provide feedback - no popup needed
        } catch (error) {
            console.error('Error saving draft:', error);
            message.error(t('messages.errors.saveFailed'));
        }
    };

    // Add function to handle "Save as Template" button click
    const handleSaveAsTemplate = async () => {
        try {
            // Mark as modified to ensure it saves
            formModifiedRef.current = true;
            
            const formValues = form.getFieldsValue();
            
            // Process recipients
            let recipientValue = '';
            let ccValue = '';
            let bccValue = '';
            
            if (formValues.recipient && Array.isArray(formValues.recipient)) {
                recipientValue = formValues.recipient.join(', ');
            } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                recipientValue = formValues.recipient.trim();
            }
            
            if (formValues.cc && Array.isArray(formValues.cc)) {
                ccValue = formValues.cc.join(', ');
            } else if (formValues.cc && typeof formValues.cc === 'string') {
                ccValue = formValues.cc.trim();
            }
            
            if (formValues.bcc && Array.isArray(formValues.bcc)) {
                bccValue = formValues.bcc.join(', ');
            } else if (formValues.bcc && typeof formValues.bcc === 'string') {
                bccValue = formValues.bcc.trim();
            }
            
            // Prepare data for sending (new template - optionally copy from draft)
            const params = new URLSearchParams();
            const urlParams = new URLSearchParams(window.location.search);
            const isEditingTemplate = !!urlParams.get('template_id');
            if (draftId && !isEditingTemplate) {
                params.append('source_draft_id', draftId);
            }
            params.append('subject', formValues.subject || '');
            params.append('message', formValues.message || '');
            params.append('recipient', recipientValue);
            params.append('cc', ccValue);
            params.append('bcc', bccValue);
            params.append('is_template', 'true');
            params.append('self_destruct', formValues.self_destruct || false);
            if (formValues.timestamp) {
                params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            params.append('prevent_message_copy', preventCopyEnabled.toString());

            const fileList = messageEditorRef.current?.getFileList() || [];
            const draftFileIds = fileList.filter((f) => f.isDraftFile).map((f) => f.uid);
            const newFiles = fileList.filter((f) => !f.isDraftFile && (f.originFileObj || f));
            params.append('existing_draft_file_ids', JSON.stringify(draftFileIds));
            const attachmentDraftCommentsPayload = {};
            draftFileIds.forEach((fid) => {
                const list = draftFileComments[fid];
                if (Array.isArray(list) && list.length > 0) {
                    attachmentDraftCommentsPayload[fid] = flattenDraftComments(list).map((c) => ({
                        unique_identifier: c.unique_identifier,
                        content: c.content,
                        page: c.page ?? 1,
                        position_x: c.position_x ?? 50,
                        position_y: c.position_y ?? 50,
                        width: c.width ?? 25,
                        height: c.height ?? 10,
                        parent_comment_id: c.parent_comment_id || null,
                    }));
                }
            });
            params.append('attachment_draft_comments', JSON.stringify(attachmentDraftCommentsPayload));
            const attachmentLocalCommentsPayload = newFiles.map((f) => {
                const list = localFileComments[f.uid] || [];
                return Array.isArray(list) ? list.map((c) => ({
                    content: c.content,
                    page: c.page ?? 1,
                    position_x: c.position_x ?? 50,
                    position_y: c.position_y ?? 50,
                    width: c.width ?? 25,
                    height: c.height ?? 10,
                })) : [];
            });
            params.append('attachment_local_comments', JSON.stringify(attachmentLocalCommentsPayload));

            if (newFiles.length > 0) {
                const formData = new FormData();
                for (const [k, v] of params.entries()) formData.append(k, v);
                newFiles.forEach((f) => {
                    const raw = f.originFileObj || f;
                    if (raw) formData.append('attachments', raw, raw.name || f.name || 'file');
                });
                await axios.post('/api/save_message_draft/', formData);
            } else {
                await axios.post('/api/save_message_draft/', params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });
            }
            message.success(t('messages.send.templateSaved'));
            
        } catch (error) {
            console.error('Error saving template:', error);
            message.error(t('messages.errors.saveFailed'));
        }
    };

    // Add function to handle "Update Template" button click
    const handleUpdateTemplate = async () => {
        try {
            setIsSaving(true);
            const formValues = form.getFieldsValue();
            
            // Process recipients
            let recipientValue = '';
            let ccValue = '';
            let bccValue = '';
            
            if (formValues.recipient && Array.isArray(formValues.recipient)) {
                recipientValue = formValues.recipient.join(', ');
            } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                recipientValue = formValues.recipient.trim();
            }
            
            if (formValues.cc && Array.isArray(formValues.cc)) {
                ccValue = formValues.cc.join(', ');
            } else if (formValues.cc && typeof formValues.cc === 'string') {
                ccValue = formValues.cc.trim();
            }
            
            if (formValues.bcc && Array.isArray(formValues.bcc)) {
                bccValue = formValues.bcc.join(', ');
            } else if (formValues.bcc && typeof formValues.bcc === 'string') {
                bccValue = formValues.bcc.trim();
            }
            
            // Prepare data for sending (update existing template)
            const params = new URLSearchParams();
            params.append('draft_id', draftId);
            params.append('subject', formValues.subject || '');
            params.append('message', formValues.message || '');
            params.append('recipient', recipientValue);
            params.append('cc', ccValue);
            params.append('bcc', bccValue);
            params.append('is_template', 'true');
            params.append('self_destruct', formValues.self_destruct || false);
            if (formValues.timestamp) {
                params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            params.append('prevent_message_copy', preventCopyEnabled.toString());

            const fileList = messageEditorRef.current?.getFileList() || [];
            const draftFileIds = fileList.filter((f) => f.isDraftFile).map((f) => f.uid);
            const newFiles = fileList.filter((f) => !f.isDraftFile && (f.originFileObj || f));
            params.append('existing_draft_file_ids', JSON.stringify(draftFileIds));
            const attachmentDraftCommentsPayload = {};
            draftFileIds.forEach((fid) => {
                const list = draftFileComments[fid];
                if (Array.isArray(list) && list.length > 0) {
                    attachmentDraftCommentsPayload[fid] = flattenDraftComments(list).map((c) => ({
                        unique_identifier: c.unique_identifier,
                        content: c.content,
                        page: c.page ?? 1,
                        position_x: c.position_x ?? 50,
                        position_y: c.position_y ?? 50,
                        width: c.width ?? 25,
                        height: c.height ?? 10,
                        parent_comment_id: c.parent_comment_id || null,
                    }));
                }
            });
            params.append('attachment_draft_comments', JSON.stringify(attachmentDraftCommentsPayload));
            const attachmentLocalCommentsPayloadUpdate = newFiles.map((f) => {
                const list = localFileComments[f.uid] || [];
                return Array.isArray(list) ? list.map((c) => ({
                    content: c.content,
                    page: c.page ?? 1,
                    position_x: c.position_x ?? 50,
                    position_y: c.position_y ?? 50,
                    width: c.width ?? 25,
                    height: c.height ?? 10,
                })) : [];
            });
            params.append('attachment_local_comments', JSON.stringify(attachmentLocalCommentsPayloadUpdate));

            let response;
            if (newFiles.length > 0) {
                const formData = new FormData();
                for (const [k, v] of params.entries()) formData.append(k, v);
                newFiles.forEach((f) => {
                    const raw = f.originFileObj || f;
                    if (raw) formData.append('attachments', raw, raw.name || f.name || 'file');
                });
                response = await axios.post('/api/save_message_draft/', formData);
            } else {
                response = await axios.post('/api/save_message_draft/', params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });
            }
            
            // Update the last saved time
            if (response.data.updated_at) {
                const savedTime = dayjs(response.data.updated_at);
                setLastSavedTime(savedTime);
                updateDisplayTimestamp(savedTime);
            }
            
            // Keep the saving indicator visible for 3 seconds
            setTimeout(() => {
                setIsSaving(false);
            }, 3000);
            
            // Reset the modified flag since we just saved
            formModifiedRef.current = false;
            
        } catch (error) {
            console.error('Error updating template:', error);
            setIsSaving(false);
            message.error(t('messages.errors.saveFailed'));
        }
    };

    // Add function to handle "Save as Draft" button click (creates new draft from template)
    const handleSaveAsDraftFromTemplate = async () => {
        try {
            // Mark as modified to ensure it saves
            formModifiedRef.current = true;
            
            const formValues = form.getFieldsValue();
            
            // Process recipients
            let recipientValue = '';
            let ccValue = '';
            let bccValue = '';
            
            if (formValues.recipient && Array.isArray(formValues.recipient)) {
                recipientValue = formValues.recipient.join(', ');
            } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                recipientValue = formValues.recipient.trim();
            }
            
            if (formValues.cc && Array.isArray(formValues.cc)) {
                ccValue = formValues.cc.join(', ');
            } else if (formValues.cc && typeof formValues.cc === 'string') {
                ccValue = formValues.cc.trim();
            }
            
            if (formValues.bcc && Array.isArray(formValues.bcc)) {
                bccValue = formValues.bcc.join(', ');
            } else if (formValues.bcc && typeof formValues.bcc === 'string') {
                bccValue = formValues.bcc.trim();
            }
            
            // Prepare data for sending - NO draft_id to create new draft, NO is_template
            const params = new URLSearchParams();
            params.append('subject', formValues.subject || '');
            params.append('message', formValues.message || '');
            params.append('recipient', recipientValue);
            params.append('cc', ccValue);
            params.append('bcc', bccValue);
            
            params.append('self_destruct', formValues.self_destruct || false);
            
            if (formValues.timestamp) {
                params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            
            
            // Include email notifications setting
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            
            // Include prevent copy setting
            params.append('prevent_message_copy', preventCopyEnabled.toString());
            
            await axios.post('/api/save_message_draft/', params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            message.success(t('messages.send.draftSaved'));
            
        } catch (error) {
            console.error('Error saving draft from template:', error);
            message.error(t('messages.errors.saveFailed'));
        }
    };

    // Add function to handle cancel with special logic for originally sent messages
    const handleCancel = async () => {
        try {
            // Only revert to inbox if user came from sent folder AND it's an originally sent message AND it's currently in drafts
            // Since messages now only move to drafts when saved, we need to check if it was actually moved
            if (cameFromSentFolder && isOriginallySentMessage && draftId) {
                // Check if the message is currently in drafts folder before reverting
                try {
                    await axios.get(`/api/get_message_draft/${draftId}/`);
                    // If we can get it as a draft or it's an email in drafts folder, then revert it
                    await axios.post(`/api/message/revert/${draftId}/`);
                } catch (error) {
                    // If the draft/email doesn't exist or isn't in drafts, don't try to revert
                    console.log('No need to revert - message was not moved to drafts');
                }
            }
            
            // Clear localStorage draft ID and edit source
            localStorage.removeItem('currentDraftId');
            localStorage.removeItem('editSource');
            
            // Navigate back to mailbox
            navigate('/profile/mailbox');
        } catch (error) {
            console.error('Error reverting message:', error);
            // Still navigate even if revert fails
            localStorage.removeItem('editSource');
            navigate('/profile/mailbox');
        }
    };

    return (
        <>
        <div 
            className={`message-compose-container ${isDraggingFiles ? 'dragging-files' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {/* Drag and Drop Overlay */}
            {isDraggingFiles && !isLimitedEdit && (
                <div className="message-compose-drop-overlay">
                    <div className="message-compose-drop-content">
                        <CloudUploadOutlined className="message-compose-drop-icon" />
                        <h2 className="message-compose-drop-title">{t('messages.send.editor.dropFilesHere')}</h2>
                        <p className="message-compose-drop-subtitle">{t('messages.send.editor.dropFilesSubtitle')}</p>
                    </div>
                </div>
            )}
            {/* Top section - matches DocumentCompose */}
            <div className="message-compose-top-header">
                <div className="message-compose-header">
                        <div className="message-compose-header-left">
                            {/* Draft Status Indicator */}
                            {isSaving && !isLimitedEdit && (
                                <div className="message-compose-draft-status-indicator saving">
                                    <SaveOutlined /> 
                                    <span>
                                        {isTemplate() ? t('messages.send.savingTemplate') : t('messages.send.savingDraft')}
                                    </span>
                                </div>
                            )}
                            
                            {lastSavedTime && !isSaving && !isLimitedEdit && autoSaveEnabled && !isTemplate() && (
                                <div className="message-compose-draft-status-indicator saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('messages.send.draftSavedAutoSave', { time: displayTimestamp })}
                                    </span>
                                </div>
                            )}
                            
                            {lastSavedTime && !isSaving && !isLimitedEdit && isTemplate() && (
                                <div className="message-compose-draft-status-indicator saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('messages.send.templateSavedWithTime', { time: displayTimestamp })}
                                    </span>
                                </div>
                            )}
                            
                            {lastSavedTime && !isSaving && !isLimitedEdit && !autoSaveEnabled && !isTemplate() && (
                                <div className="message-compose-draft-status-indicator saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('messages.send.draftSavedWithTime', { time: displayTimestamp })}
                                    </span>
                                </div>
                            )}
                            
                            {!lastSavedTime && !isSaving && !loadingDraft && !isLimitedEdit && !isTemplate() && (
                                <div className="message-compose-draft-status-indicator not-saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('messages.send.draftNotSaved')}
                                    </span>
                                </div>
                            )}
                            
                            {!lastSavedTime && !isSaving && !loadingDraft && !isLimitedEdit && isTemplate() && (
                                <div className="message-compose-draft-status-indicator not-saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('messages.send.templateNotSaved')}
                                    </span>
                                </div>
                            )}
                            
                            {isLimitedEdit && (
                                <div className="message-compose-draft-status-indicator limited">
                                    <InfoCircleOutlined />
                                    <span>{t('messages.send.titleEditLimited')}</span>
                                </div>
                            )}
                        </div>
                        <div className="message-compose-header-center">
                            {!loadingDraft && !isLimitedEdit && (
                                <Select
                                    placeholder={t('messages.send.selectTemplate') || 'Select a template'}
                                    loading={templatesLoading}
                                    allowClear
                                    className="message-compose-template-select message-compose-header-template-select"
                                    options={templates.map(tpl => ({
                                        value: tpl.access_link,
                                        label: tpl.subject || t('messages.send.unnamedTemplate') || 'Unnamed template'
                                    }))}
                                    onSelect={(accessLink) => {
                                        window.location.href = accessLink;
                                    }}
                                />
                            )}
                        </div>
                        <div className="message-compose-header-actions">
                            <Space>
                                {!isLimitedEdit && !isTemplate() && (
                                    <Button 
                                        type="default" 
                                        className="btn-save"
                                        onClick={handleSaveAsTemplate}
                                    >
                                        {t('messages.send.saveAsTemplate')}
                                    </Button>
                                )}
                                {!isLimitedEdit && isTemplate() && (
                                    <Button 
                                        type="default" 
                                        className="btn-save"
                                        onClick={handleUpdateTemplate}
                                        loading={isSaving}
                                        disabled={!draftId && autoSaveEnabled && lastSavedTime && !formModifiedRef.current}
                                    >
                                        {t('messages.send.updateTemplate')}
                                    </Button>
                                )}
                                {!isLimitedEdit && isTemplate() && (
                                    <Button 
                                        type="default" 
                                        className="btn-save"
                                        onClick={handleSaveAsDraftFromTemplate}
                                    >
                                        {t('messages.send.saveAsDraftFromTemplate')}
                                    </Button>
                                )}
                                {!isLimitedEdit && !isTemplate() && (
                                    <Button 
                                        type="default" 
                                        className="btn-save"
                                        onClick={handleSaveAsDraft}
                                        loading={isSaving}
                                        disabled={!draftId && autoSaveEnabled && lastSavedTime && !formModifiedRef.current}
                                    >
                                        {draftId && autoSaveEnabled ? t('messages.send.updateDraft') : t('messages.send.saveAsDraft')}
                                    </Button>
                                )}
                                <Button 
                                    type="primary" 
                                    loading={submitLoading}
                                    htmlType="submit"
                                    form="message-compose-form"
                                >
                                    {isLimitedEdit 
                                        ? t('messages.send.updateMessage') 
                                        : (hasRecipients ? t('messages.send.sendMessage') : t('messages.send.createLink'))
                                    }
                                </Button>
                                <Button 
                                    type="default" 
                                    className="btn-cancel"
                                    icon={<CloseOutlined />}
                                    onClick={handleCancel}
                                    title={t('messages.send.cancel')}
                                />
                            </Space>
                        </div>
                    </div>
            </div>
            <div className="message-compose-detail-body-wrapper">
                <Form
                    id="message-compose-form"
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                    preserve={true}
                    requiredMark={(label, { required }) => required ? <>{label} <span className="message-compose-required-asterisk">*</span></> : label}
                    onValuesChange={() => {
                        if (!initialLoadRef.current) {
                            formModifiedRef.current = true;
                            console.log('Form values changed, triggering auto-save. Auto-save enabled:', autoSaveEnabled);
                            triggerAutoSave();
                        }
                    }}
                >
                    {/* Top Section - Form Fields */}
                    <div className={`message-compose-form-top ${formTopCollapsed ? 'collapsed' : ''}`}>
                        {/* Row 1: Receivers (full width) */}
                        <div className="message-compose-receivers-row">
                            <div className="message-compose-receivers-section">
                                <Form.Item
                                    name="recipient"
                                    preserve={true}
                                >
                                    <div className="message-compose-receiver-input-row">
                                        <div className="message-compose-recipient-field">
                                            <Select
                                            mode="tags"
                                            className="message-compose-input"
                                            placeholder={t('messages.send.recipientOptionalPlaceholder')}
                                            tokenSeparators={[',', ';', ' ']}
                                            allowClear
                                            showSearch
                                            open={recipientDropdownOpen}
                                            value={recipientTags}
                                            searchValue={recipientSearchText}
                                            autoClearSearchValue={false}
                                            listHeight={200}
                                            notFoundContent={null}
                                            suffixIcon={null}
                                            disabled={isLimitedEdit}
                                            onKeyDown={(e) => {
                                                if ((e.key === 'Tab' || e.key === ';' || e.key === ' ') && recipientSearchText && recipientSearchText.trim() !== '') {
                                                    e.preventDefault();
                                                    const trimmedEmail = recipientSearchText.trim();
                                                    if (validateEmail(trimmedEmail)) {
                                                        const newTags = [...recipientTags, trimmedEmail];
                                                        setRecipientTags(newTags);
                                                        form.setFieldsValue({ recipient: newTags });
                                                        setRecipientSearchText('');
                                                        setHasRecipients(true);
                                                    } else if (trimmedEmail.includes('@')) {
                                                        message.error(t('messages.validation.invalidEmail', { email: trimmedEmail }));
                                                    }
                                                }
                                            }}
                                            onFocus={() => setRecipientDropdownOpen(true)}
                                            onDropdownVisibleChange={(open) => setRecipientDropdownOpen(open)}
                                            onSearch={(text) => setRecipientSearchText(text)}
                                            dropdownRender={() => (
                                                <ContactsDropdown
                                                    contacts={filteredRecipientContacts}
                                                    selectedTags={recipientTags}
                                                    field="recipient"
                                                    onSelect={handleContactSelect}
                                                    onAddFavorite={handleContactAddFavorite}
                                                    onRemoveFavorite={handleContactRemoveFavorite}
                                                    onEdit={handleContactEdit}
                                                    onDelete={handleContactDelete}
                                                    isLoading={contactsLoading}
                                                    t={t}
                                                />
                                            )}
                                            onChange={(value) => {
                                                if (selectingContact) return;
                                                if (JSON.stringify(value) === JSON.stringify(recipientTags)) return;
                                                const filteredValue = value.filter(item => {
                                                    const trimmedItem = typeof item === 'string' ? item.trim() : '';
                                                    const trimmedSearchText = recipientSearchText ? recipientSearchText.trim() : '';
                                                    if (trimmedItem === trimmedSearchText && !validateEmail(trimmedItem)) return false;
                                                    return true;
                                                });
                                                if (filteredValue.length > recipientTags.length) {
                                                    const newItem = filteredValue[filteredValue.length - 1];
                                                    if (newItem && typeof newItem === 'string') {
                                                        const trimmedEmail = newItem.trim();
                                                        if (trimmedEmail === '') {
                                                            const cleanedValues = filteredValue.filter(email => email !== newItem);
                                                            form.setFieldsValue({ recipient: cleanedValues });
                                                            setRecipientTags(cleanedValues);
                                                            return;
                                                        }
                                                        const isValid = validateEmail(trimmedEmail);
                                                        if (!isValid) {
                                                            if (trimmedEmail.includes('@')) {
                                                                message.error(t('messages.validation.invalidEmail', { email: trimmedEmail }));
                                                            } else if (trimmedEmail.length > 3 || trimmedEmail.includes('.')) {
                                                                message.error(t('messages.validation.missingAtSymbol', { email: trimmedEmail }));
                                                            }
                                                            const cleanedValues = filteredValue.filter(email => email !== newItem);
                                                            form.setFieldsValue({ recipient: cleanedValues });
                                                            setRecipientTags(cleanedValues);
                                                            return;
                                                        } else {
                                                            setRecipientSearchText('');
                                                        }
                                                    }
                                                }
                                                const cleanedValues = filteredValue.map(email => 
                                                    typeof email === 'string' ? email.trim() : email
                                                ).filter(email => email !== '');
                                                form.setFieldsValue({ recipient: cleanedValues });
                                                setRecipientTags(cleanedValues);
                                                setHasRecipients(cleanedValues.length > 0);
                                                if (cleanedValues.length === 0) {
                                                    form.setFieldsValue({ cc: [], bcc: [] });
                                                    setCcTags([]);
                                                    setBccTags([]);
                                                    setShowCcBcc(false);
                                                }
                                            }}
                                        />
                                        </div>
                                        {/* CC/BCC Toggle (inline with input) - only enabled when there are recipients */}
                                        <div className="message-compose-ccbcc-toggle">
                                            <Tooltip title={!hasRecipients ? t('messages.send.addRecipientFirst') : ''}>
                                                <div 
                                                    className={`message-compose-ccbcc-arrow-toggle ${!hasRecipients ? 'disabled' : ''}`}
                                                    onClick={() => hasRecipients && setShowCcBcc(!showCcBcc)}
                                                >
                                                    <DownOutlined className={`message-compose-ccbcc-arrow ${showCcBcc ? 'rotated' : ''}`} />
                                                    <span>CC/BCC</span>
                                                    {(ccTags?.length || 0) + (bccTags?.length || 0) > 0 && (
                                                        <span className="message-compose-ccbcc-counter">
                                                            {(ccTags?.length || 0) + (bccTags?.length || 0)}
                                                        </span>
                                                    )}
                                                </div>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </Form.Item>

                                {/* CC/BCC Fields - shown when toggled */}
                                <div className={`message-compose-ccbcc-fields ${showCcBcc ? 'visible' : ''}`}>
                                    <Form.Item name="cc" preserve={true} style={{ marginBottom: 12 }}>
                                        <Select
                                            mode="tags"
                                            className="message-compose-input"
                                            placeholder={t('messages.send.ccPlaceholder')}
                                            tokenSeparators={[',', ';', ' ']}
                                            allowClear
                                            disabled={!hasRecipients || isLimitedEdit}
                                            showSearch
                                            open={ccDropdownOpen}
                                            value={ccTags}
                                            searchValue={ccSearchText}
                                            autoClearSearchValue={false}
                                            listHeight={200}
                                            notFoundContent={null}
                                            suffixIcon={null}
                                            onKeyDown={(e) => {
                                                if ((e.key === 'Tab' || e.key === ';' || e.key === ' ') && ccSearchText && ccSearchText.trim() !== '') {
                                                    e.preventDefault();
                                                    const trimmedEmail = ccSearchText.trim();
                                                    if (validateEmail(trimmedEmail)) {
                                                        const newTags = [...ccTags, trimmedEmail];
                                                        setCcTags(newTags);
                                                        form.setFieldsValue({ cc: newTags });
                                                        setCcSearchText('');
                                                    } else if (trimmedEmail.includes('@')) {
                                                        message.error(t('messages.validation.invalidEmail', { email: trimmedEmail }));
                                                    }
                                                }
                                            }}
                                            onFocus={() => setCcDropdownOpen(true)}
                                            onDropdownVisibleChange={(open) => setCcDropdownOpen(open)}
                                            onSearch={(text) => setCcSearchText(text)}
                                            dropdownRender={() => (
                                                <ContactsDropdown
                                                    contacts={filteredCcContacts}
                                                    selectedTags={ccTags}
                                                    field="cc"
                                                    onSelect={handleContactSelect}
                                                    onAddFavorite={handleContactAddFavorite}
                                                    onRemoveFavorite={handleContactRemoveFavorite}
                                                    onEdit={handleContactEdit}
                                                    onDelete={handleContactDelete}
                                                    isLoading={contactsLoading}
                                                    t={t}
                                                />
                                            )}
                                            onChange={(value) => {
                                                if (selectingContact) return;
                                                if (JSON.stringify(value) === JSON.stringify(ccTags)) return;
                                                const filteredValue = value.filter(item => {
                                                    const trimmedItem = typeof item === 'string' ? item.trim() : '';
                                                    const trimmedSearchText = ccSearchText ? ccSearchText.trim() : '';
                                                    if (trimmedItem === trimmedSearchText && !validateEmail(trimmedItem)) return false;
                                                    return true;
                                                });
                                                if (filteredValue.length > ccTags.length) {
                                                    const newItem = filteredValue[filteredValue.length - 1];
                                                    if (newItem && typeof newItem === 'string') {
                                                        const trimmedEmail = newItem.trim();
                                                        if (trimmedEmail === '') {
                                                            const cleanedValues = filteredValue.filter(email => email !== newItem);
                                                            form.setFieldsValue({ cc: cleanedValues });
                                                            setCcTags(cleanedValues);
                                                            return;
                                                        }
                                                        const isValid = validateEmail(trimmedEmail);
                                                        if (!isValid) {
                                                            if (trimmedEmail.includes('@')) {
                                                                message.error(t('messages.validation.invalidEmail', { email: trimmedEmail }));
                                                            } else if (trimmedEmail.length > 3 || trimmedEmail.includes('.')) {
                                                                message.error(t('messages.validation.missingAtSymbol', { email: trimmedEmail }));
                                                            }
                                                            const cleanedValues = filteredValue.filter(email => email !== newItem);
                                                            form.setFieldsValue({ cc: cleanedValues });
                                                            setCcTags(cleanedValues);
                                                            return;
                                                        } else {
                                                            setCcSearchText('');
                                                        }
                                                    }
                                                }
                                                const cleanedValues = filteredValue.map(email => 
                                                    typeof email === 'string' ? email.trim() : email
                                                ).filter(email => email !== '');
                                                form.setFieldsValue({ cc: cleanedValues });
                                                setCcTags(cleanedValues);
                                            }}
                                        />
                                    </Form.Item>

                                    <Form.Item name="bcc" preserve={true} style={{ marginBottom: 0 }}>
                                        <Select
                                            mode="tags"
                                            className="message-compose-input"
                                            placeholder={t('messages.send.bccPlaceholder')}
                                            tokenSeparators={[',', ';', ' ']}
                                            allowClear
                                            disabled={!hasRecipients || isLimitedEdit}
                                            showSearch
                                            open={bccDropdownOpen}
                                            value={bccTags}
                                            searchValue={bccSearchText}
                                            autoClearSearchValue={false}
                                            listHeight={200}
                                            notFoundContent={null}
                                            suffixIcon={null}
                                            onKeyDown={(e) => {
                                                if ((e.key === 'Tab' || e.key === ';' || e.key === ' ') && bccSearchText && bccSearchText.trim() !== '') {
                                                    e.preventDefault();
                                                    const trimmedEmail = bccSearchText.trim();
                                                    if (validateEmail(trimmedEmail)) {
                                                        const newTags = [...bccTags, trimmedEmail];
                                                        setBccTags(newTags);
                                                        form.setFieldsValue({ bcc: newTags });
                                                        setBccSearchText('');
                                                    } else if (trimmedEmail.includes('@')) {
                                                        message.error(t('messages.validation.invalidEmail', { email: trimmedEmail }));
                                                    }
                                                }
                                            }}
                                            onFocus={() => setBccDropdownOpen(true)}
                                            onDropdownVisibleChange={(open) => setBccDropdownOpen(open)}
                                            onSearch={(text) => setBccSearchText(text)}
                                            dropdownRender={() => (
                                                <ContactsDropdown
                                                    contacts={filteredBccContacts}
                                                    selectedTags={bccTags}
                                                    field="bcc"
                                                    onSelect={handleContactSelect}
                                                    onAddFavorite={handleContactAddFavorite}
                                                    onRemoveFavorite={handleContactRemoveFavorite}
                                                    onEdit={handleContactEdit}
                                                    onDelete={handleContactDelete}
                                                    isLoading={contactsLoading}
                                                    t={t}
                                                />
                                            )}
                                            onChange={(value) => {
                                                if (selectingContact) return;
                                                if (JSON.stringify(value) === JSON.stringify(bccTags)) return;
                                                const filteredValue = value.filter(item => {
                                                    const trimmedItem = typeof item === 'string' ? item.trim() : '';
                                                    const trimmedSearchText = bccSearchText ? bccSearchText.trim() : '';
                                                    if (trimmedItem === trimmedSearchText && !validateEmail(trimmedItem)) return false;
                                                    return true;
                                                });
                                                if (filteredValue.length > bccTags.length) {
                                                    const newItem = filteredValue[filteredValue.length - 1];
                                                    if (newItem && typeof newItem === 'string') {
                                                        const trimmedEmail = newItem.trim();
                                                        if (trimmedEmail === '') {
                                                            const cleanedValues = filteredValue.filter(email => email !== newItem);
                                                            form.setFieldsValue({ bcc: cleanedValues });
                                                            setBccTags(cleanedValues);
                                                            return;
                                                        }
                                                        const isValid = validateEmail(trimmedEmail);
                                                        if (!isValid) {
                                                            if (trimmedEmail.includes('@')) {
                                                                message.error(t('messages.validation.invalidEmail', { email: trimmedEmail }));
                                                            } else if (trimmedEmail.length > 3 || trimmedEmail.includes('.')) {
                                                                message.error(t('messages.validation.missingAtSymbol', { email: trimmedEmail }));
                                                            }
                                                            const cleanedValues = filteredValue.filter(email => email !== newItem);
                                                            form.setFieldsValue({ bcc: cleanedValues });
                                                            setBccTags(cleanedValues);
                                                            return;
                                                        } else {
                                                            setBccSearchText('');
                                                        }
                                                    }
                                                }
                                                const cleanedValues = filteredValue.map(email => 
                                                    typeof email === 'string' ? email.trim() : email
                                                ).filter(email => email !== '');
                                                form.setFieldsValue({ bcc: cleanedValues });
                                                setBccTags(cleanedValues);
                                            }}
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                        </div>

                        {/* Row 2: Access Code (50%) | Expiry (50%) */}
                        <div className="message-compose-options-row">
                            <div className="message-compose-access-code-section" style={{ width: '100%' }}>
                                <Button
                                    type="default"
                                    icon={<LockOutlined />}
                                    onClick={() => setAccessCodeDeliveryModalOpen(true)}
                                    disabled={isLimitedEdit}
                                    className={accessCodeBtnError ? 'access-code-btn-error' : ''}
                                    style={{ width: '100%', height: 40 }}
                                >
                                    {t('compose.accessCodeDelivery.done', 'Access Code')}
                                </Button>
                            </div>
                            <Tooltip title={t('messages.send.expiryTooltip', { 
                                days: userSettings?.message_security?.message_lifetime_days || t('messages.send.loadingDraft'),
                                time: userSettings?.message_security?.daily_expiration_time || t('messages.send.loadingDraft')
                            })}>
                                <div className="message-compose-expiry-section message-compose-expiry-inline">
                                    <Form.Item
                                        name="timestamp"
                                        rules={[{ required: true, message: t('messages.validation.expiryRequired') }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <DatePicker
                                            showTime
                                            showNow={false}
                                            format="YYYY-MM-DD HH:mm:ss"
                                            style={{ width: '100%' }}
                                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                                            placeholder={t('messages.send.expiryPlaceholder')}
                                        />
                                    </Form.Item>
                                </div>
                            </Tooltip>
                        </div>

                        {/* Row 3: Subject (full width) */}
                        <div className="message-compose-subject-row">
                            <Form.Item
                                name="subject"
                                rules={[{ required: true, message: t('messages.validation.subjectRequired') }]}
                                validateTrigger={['onSubmit']}
                            >
                                <Input placeholder={t('messages.send.subjectPlaceholder')} disabled={isLimitedEdit} />
                            </Form.Item>
                        </div>

                    </div>

                    {/* Validation error (centered) + Toggles (right) on same line */}
                    <Form.Item noStyle shouldUpdate>
                        {() => {
                            const subjectError = form.getFieldError('subject')?.[0];
                            const messageError = form.getFieldError('message')?.[0];
                            const error = subjectError || messageError || '';
                            return (
                                <div className="message-compose-validation-error-row">
                                    <div className="message-compose-validation-error">
                                        {error}
                                    </div>
                                    <div className="message-compose-validation-error-toggles">
                                        <Tooltip title={t('messages.send.selfDestructTooltip')}>
                                            <div className="message-compose-toggle-card message-compose-toggle-inline">
                                                <Form.Item name="self_destruct" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                    <Switch
                                                        checkedChildren={t('messages.send.selfDestructLabel')}
                                                        unCheckedChildren={t('messages.send.selfDestructLabel')}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </Tooltip>
                                        <Tooltip title={t('messages.send.preventCopyTooltip')}>
                                            <div className="message-compose-toggle-card message-compose-toggle-inline">
                                                <Switch
                                                    checked={preventCopyEnabled}
                                                    onChange={setPreventCopyEnabled}
                                                    checkedChildren={t('messages.send.preventCopyLabel')}
                                                    unCheckedChildren={t('messages.send.preventCopyLabel')}
                                                    disabled={isLimitedEdit}
                                                />
                                            </div>
                                        </Tooltip>
                                    </div>
                                </div>
                            );
                        }}
                    </Form.Item>

                    {/* Message Editor Section - Full Width (collapse chevron is inside editor toolbar) */}
                    <div className="message-compose-editor-section">
                        <Form.Item
                            name="message"
                            rules={[{
                                validator: (_, value) => {
                                    const stripped = (value || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                                    if (!stripped) {
                                        return Promise.reject(new Error(t('messages.validation.messageRequired')));
                                    }
                                    return Promise.resolve();
                                }
                            }]}
                            validateTrigger={['onSubmit']}
                            className="message-compose-editor-form-item"
                        >
                            <MessageEditor
                                placeholder={t('messages.send.messagePlaceholder')}
                                disabled={isLimitedEdit}
                                ref={messageEditorRef}
                                signature={currentSignature}
                                attachmentsEnabled={true}
                                attachmentsAllowAdd={!isLimitedEdit}
                                attachmentsAllowRemove={!isLimitedEdit}
                                initialFileList={isLimitedEdit ? limitedEditFiles : []}
                                allowedFileTypes={settings?.allowedFileTypes || ''}
                                maxUploadSize={settings?.maxUploadSize || 10}
                                showError={(msg) => message.error(msg)}
                                formCollapsed={formTopCollapsed}
                                onFormCollapseToggle={() => setFormTopCollapsed(!formTopCollapsed)}
                                onAttachmentClick={(file) => {
                                    const name = file?.name || file?.originFileObj?.name || file?.file_name;
                                    if (!isConvertible(name)) return;
                                    setAttachmentPreviewFile(file);
                                }}
                                attachmentPreviewable={(f) => isConvertible(f.name)}
                                fileCommentsCountMap={{
                                    ...Object.fromEntries(Object.entries(localFileComments).map(([uid, list]) => [uid, (list || []).length])),
                                    ...Object.fromEntries(Object.entries(draftFileComments).map(([uid, list]) => [uid, (list || []).length])),
                                    ...(isLimitedEdit ? Object.fromEntries(limitedEditFiles.filter((f) => f.uid).map((f) => [f.uid, f.comment_count ?? 0])) : {}),
                                }}
                            />
                        </Form.Item>
                    </div>
                </Form>
            </div>
        </div>

        <AttachmentPreview
            open={!!attachmentPreviewFile}
            onClose={() => setAttachmentPreviewFile(null)}
            localFile={attachmentPreviewFile?.isDraftFile || attachmentPreviewFile?.source === 'messages' ? undefined : attachmentPreviewFile}
            fileName={attachmentPreviewFile?.name || attachmentPreviewFile?.originFileObj?.name || attachmentPreviewFile?.file_name}
            documentFileId={attachmentPreviewFile?.source === 'messages' && !attachmentPreviewFile?.isDraftFile ? (attachmentPreviewFile.documentFileId || attachmentPreviewFile.uid) : undefined}
            source={attachmentPreviewFile?.source === 'messages' && !attachmentPreviewFile?.isDraftFile ? 'messages' : undefined}
            localComments={attachmentPreviewFile?.isDraftFile || attachmentPreviewFile?.source === 'messages' ? undefined : (attachmentPreviewFile?.uid ? localFileComments[attachmentPreviewFile.uid] : undefined)}
            onLocalCommentsChange={attachmentPreviewFile?.isDraftFile || attachmentPreviewFile?.source === 'messages' ? undefined : (attachmentPreviewFile?.uid ? (comments) => setLocalFileComments((prev) => ({ ...prev, [attachmentPreviewFile.uid]: comments })) : undefined)}
            draftFileId={attachmentPreviewFile?.isDraftFile ? attachmentPreviewFile.uid : undefined}
            draftDownloadUrl={attachmentPreviewFile?.isDraftFile ? (attachmentPreviewFile.download_url || `/api/drafts/files/${attachmentPreviewFile.uid}/`) : undefined}
            draftFileComments={attachmentPreviewFile?.isDraftFile && attachmentPreviewFile?.uid ? draftFileComments[attachmentPreviewFile.uid] : undefined}
            onDraftFileCommentsChange={attachmentPreviewFile?.isDraftFile && attachmentPreviewFile?.uid ? (comments) => handleDraftFileCommentsChange(attachmentPreviewFile.uid, comments) : undefined}
        />

        <AccessCodeDeliveryModal
            open={accessCodeDeliveryModalOpen}
            onClose={() => setAccessCodeDeliveryModalOpen(false)}
            onSave={handleAccessCodeModalSave}
            recipients={{ to: recipientTags, cc: ccTags, bcc: bccTags }}
            accessCodeDelivery={accessCodeDelivery}
            onAccessCodeDeliveryChange={setAccessCodeDelivery}
            accessCodeSmsPhones={accessCodeSmsPhones}
            onAccessCodeSmsPhonesChange={setAccessCodeSmsPhones}
            contacts={contacts}
        />

        <ContactGroupModal
            visible={contactEditModalVisible}
            onClose={handleContactEditModalClose}
            onAdded={handleContactEditModalSaved}
            type={contactEditingContact?._type || 'contact'}
            availableContacts={contacts}
            editingContact={contactEditingContact}
        />
        </>
    );
};

export default MessageCompose;